import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import { supabase } from "@/integrations/supabase/client";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/reviews")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("reviews")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Reviews — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "Real customer reviews of Glow Long glow-in-the-dark golf balls." },
      { property: "og:title", content: loaderData?.title || "Customer Reviews — Glow Long Golf" },
      { property: "og:description", content: loaderData?.meta_description || "What golfers say about Glow Long glow-in-the-dark golf balls." },
      { property: "og:url", content: "https://glowlonggolf.com/reviews" },
      { property: "og:image", content: SHARE_IMAGES.fairwayArc },
      { name: "twitter:image", content: SHARE_IMAGES.fairwayArc },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/reviews" }],

    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Glow Long Golf",
        url: "/",
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "120" },
      }),
    }],
  }),
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: () => {
    const { data: reviews } = useQuery({
      queryKey: ["all-reviews"],
      queryFn: async () => {
        const { data } = await supabase.from("reviews").select("*").eq("is_approved", true).order("created_at", { ascending: false });
        return data ?? [];
      },
    });
    return (
      <EditablePageShell slug="reviews" defaults={{ eyebrow: "Reviews", title: "What customers say" }}>
        <section className="mx-auto max-w-4xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-2">
            {(reviews ?? []).map((r: any) => (
              <article key={r.id} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex gap-1">{[...Array(r.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
                <h3 className="mt-2 font-semibold">{r.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
                <p className="mt-2 text-xs text-muted-foreground">— {r.author_name}{r.author_location ? `, ${r.author_location}` : ""}</p>
              </article>
            ))}
          </div>
        </section>
      </EditablePageShell>
    );
  },
});
